import React from 'react';
import { AxiosError } from 'axios';
import { TenantData, TenantSearchableResponse } from 'App.types';
import { apiUrl } from 'utils/apiUrl';
import { useTenantListApi } from './useTenantListApi';

export interface TenantIndex {
  getTenant: (id: string) => TenantData | undefined;
  getChildren: (id: string) => TenantData[];
}

export interface UseTenantIndexResult {
  /** Undefined while the tenant list is loading or when it failed to load */
  index?: TenantIndex;
  loading: boolean;
  error?: AxiosError;
}

// The tenant list response is cached and shared, so the index is built once per response
const indexCache = new WeakMap<TenantSearchableResponse, TenantIndex>();

const buildIndex = (response: TenantSearchableResponse): TenantIndex => {
  const byId = new Map(response.results.map((t) => [t.id, t]));

  return {
    getTenant: (id) => byId.get(id),
    getChildren: (id) =>
      (byId.get(id)?.tenantChildren ?? [])
        .map((childId) => byId.get(childId))
        .filter((child): child is TenantData => !!child),
  };
};

/**
 * Gives access to all tenants without a request per tenant.
 * Uses the full tenant list, which is loaded once and shared by the whole app.
 */
export const useTenantIndex = (): UseTenantIndexResult => {
  const { data, error, fetch } = useTenantListApi<TenantSearchableResponse>(
    apiUrl('tenancy', 'tenants/searchable'),
  );

  React.useEffect(() => {
    // Errors are returned in `error`
    fetch().catch(() => {});
  }, [fetch]);

  const index = React.useMemo(() => {
    if (!data) return undefined;
    if (!indexCache.has(data)) indexCache.set(data, buildIndex(data));
    return indexCache.get(data);
  }, [data]);

  return { index, loading: !index && !error, error };
};
